import React, { useState } from "react";
import pokemons from "./pokemonData";
import items from "./itemData";
import "./styles/TeamCommBuilder.css";

const API_BASE_URL = "https://poke-meta.com"; // API-URL für lokale Entwicklung

const TeamCommBuilder = () => {
  const [grid, setGrid] = useState(Array(4).fill().map(() => Array(5).fill(null)));
  const [extraPokemons, setExtraPokemons] = useState(Array(2).fill(null));
  const [extraTiles, setExtraTiles] = useState(Array(2).fill().map(() => Array(3).fill(null)));
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [description, setDescription] = useState("");
  const [showItemSelection, setShowItemSelection] = useState(null);
  const [successMessage, setSuccessMessage] = useState(""); // ✅ Erfolgsnachricht
  const [hideSaveButton, setHideSaveButton] = useState(false); // 
  

  // **Pokémon auswählen**
  const handleSelectPokemon = (pokemon) => {
    setSelectedPokemon(pokemon);
  };

  // **Pokémon ins Grid setzen oder entwickeln**
  const handleGridClick = (rowIndex, colIndex) => {
    const newGrid = grid.map((row) => [...row]);
    const cell = newGrid[rowIndex][colIndex];

    if (cell) {
      const nextStage = (cell.stage + 1) % cell.images.length;
      newGrid[rowIndex][colIndex] = { ...cell, stage: nextStage };
    } else if (selectedPokemon) {
      newGrid[rowIndex][colIndex] = { ...selectedPokemon, stage: 0 };
      setSelectedPokemon(null);
    }

    setGrid(newGrid);
  };

  // **Rechtsklick entfernt Pokémon**
  const handleRemovePokemon = (rowIndex, colIndex, event) => {
    event.preventDefault();
    const newGrid = grid.map((row, r) =>
      r === rowIndex ? row.map((cell, c) => (c === colIndex ? null : cell)) : row
    );
    setGrid(newGrid);
  };

  // **Extra Pokémon setzen oder entwickeln**
  const handleExtraPokemonClick = (index) => {
    const newExtraPokemons = [...extraPokemons];
    const cell = newExtraPokemons[index];

    if (cell) {
      const nextStage = (cell.stage + 1) % cell.images.length;
      newExtraPokemons[index] = { ...cell, stage: nextStage };
    } else if (selectedPokemon) {
      newExtraPokemons[index] = { ...selectedPokemon, stage: 0 };
      setSelectedPokemon(null);
    }

    setExtraPokemons(newExtraPokemons);
  };

  // **Rechtsklick entfernt Extra Pokémon**
  const handleRemoveExtraPokemon = (index, event) => {
    event.preventDefault();
    const newExtraPokemons = [...extraPokemons];
    newExtraPokemons[index] = null;
    setExtraPokemons(newExtraPokemons);
  };

  // **Helditems auswählen**
  const handleSelectItem = (rowIndex, tileIndex) => {
    setShowItemSelection({ rowIndex, tileIndex });
  };

  const handleItemClick = (item) => {
    if (!showItemSelection) return;

    const { rowIndex, tileIndex } = showItemSelection;
    const newExtraTiles = extraTiles.map((row) => [...row]);
    newExtraTiles[rowIndex][tileIndex] = item.image;
    setExtraTiles(newExtraTiles);
    setShowItemSelection(null);
  };

// **State, um zu verhindern, dass mehrfach gespeichert wird**
const [isSaving, setIsSaving] = useState(false);

const handleSaveTeam = async () => {
  if (!description.trim()) {
    alert("Bitte eine Beschreibung eingeben!");
    return;
  }

  // 🔹 Zählt alle gesetzten Pokémon im Grid
  const placedPokemon = grid.flat().filter((cell) => cell !== null);

  if (placedPokemon.length !== 6) {
    alert("Bitte setze genau 6 Pokémon auf das Grid ein.");
    return;
  }

  if (isSaving) return;

  setIsSaving(true); 
  setHideSaveButton(true);

  const payload = {
    pokemon_positions: grid
      .flatMap((row, rowIndex) =>
        row.map((cell, colIndex) =>
          cell ? { pos: rowIndex * 5 + colIndex + 1, image: cell.images[cell.stage] } : null
        )
      )
      .filter(Boolean),

    extra_pokemons: extraPokemons
      .map((p) => (p ? { image: p.images[p.stage] } : null))
      .filter(Boolean),

    extra_tiles: extraTiles.map((row) => ({
      items: row.map((tile) => (tile ? tile : "None")),
    })),

    description: description,
  };

  try {
    const response = await fetch(`${API_BASE_URL}/api/teambuilder`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    setSuccessMessage("TEAM SAVED! Grid is cleared ✅");
    setTimeout(() => setSuccessMessage(""), 3000);

    setDescription("");
    setGrid(Array(4).fill().map(() => Array(5).fill(null)));
    setExtraPokemons(Array(2).fill(null));
    setExtraTiles(Array(2).fill().map(() => Array(3).fill(null)));

    setTimeout(() => setHideSaveButton(false), 3000);
  } catch (err) {
    console.error("Fehler beim Speichern:", err);
    alert("Fehler beim Speichern. Bitte versuche es erneut.");
    setHideSaveButton(false);
  } finally {
    setIsSaving(false);
  }
};



  // **Pokémon in Gruppen von 9 pro Zeile anzeigen**
  const groupedPokemons = pokemons.reduce((rows, pokemon, index) => {
    let rowIndex = Math.floor(index / 9);
    if (!rows[rowIndex]) rows[rowIndex] = [];
    rows[rowIndex].push(pokemon);
    return rows;
  }, []);

  return (
    <div className="teamcomps-container">
      <h1 className="teamcomps-title">Community Teambuilder</h1>

      {/* Eingabefeld für die Beschreibung */}
      <div className="teamcomp-selection">
        <input
          placeholder="Username oder Teamname"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {/* Teambuilder 5x5 Grid */}
      <div className="grid-wrapper">
        {grid.map((row, rowIndex) => (
          <div key={rowIndex} className={`grid-row ${rowIndex % 2 === 1 ? "offset" : ""}`}>
            {row.map((cell, colIndex) => (
              <div 
                key={colIndex} 
                className="grid-cell" 
                onClick={() => handleGridClick(rowIndex, colIndex)}
                onContextMenu={(event) => handleRemovePokemon(rowIndex, colIndex, event)}
              >
                {cell ? <img src={cell.images[cell.stage]} alt={cell.name} className="grid-pokemon" /> : null}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Extra Pokémon & Helditems */}
      <div className="extra-pokemon-box">
        <h2>Optimal Pokémon & Helditems:</h2>
        {extraPokemons.map((cell, index) => (
          <div key={index} className="extra-pokemon-row">
            <div 
              className="extra-pokemon-slot" 
              onClick={() => handleExtraPokemonClick(index)}
              onContextMenu={(event) => handleRemoveExtraPokemon(index, event)}
            >
              {cell ? <img src={cell.images[cell.stage]} alt={cell.name} className="grid-pokemon" /> : ""}
            </div>
            <div className="extra-tiles">
              {extraTiles[index].map((tile, tileIndex) => (
                <div key={tileIndex} className="extra-tile" onClick={() => handleSelectItem(index, tileIndex)}>
                  {tile ? <img src={tile} alt="Item" className="helditem" /> : ""}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Pokémon Auswahl */}
      <div className="pokemon-selection">
        <h2>Choose Pokémons:</h2>
        {groupedPokemons.map((row, rowIndex) => (
          <div key={rowIndex} className="pokemon-row">
            {row.map((pokemon) => (
              <div key={pokemon.id} className="pokemon-slot" onClick={() => handleSelectPokemon(pokemon)}>
                <img src={pokemon.images[0]} alt={pokemon.name} className="pokemon-item" />
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {successMessage && <p className="success-message">{successMessage}</p>}

      {/* Speichern-Button */}
      {!hideSaveButton && (
      <button className="save-button" onClick={handleSaveTeam} disabled={isSaving}>
      {isSaving ? "Saving..." : "Save Team"}
      </button>)}

      {/* Helditem-Modal */}
      {showItemSelection && (
        <div className="modal">
          <div className="modal-content">
            <h2>Choose Helditem</h2>
            <div className="item-selection">
              {items.map((item) => (
                <img key={item.id} src={item.image} alt={`Item ${item.id}`} className="helditem-option"
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>
            <button onClick={() => setShowItemSelection(null)} className="close-button">close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCommBuilder;
